import { Link } from '@tanstack/react-router';
import { CheckCircle2, FileText, Zap } from 'lucide-react';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';

export type PaymentProcessingJourneyVariant = 'auto' | 'manual';

const AUTO_JOURNEY_STEPS = [
  {
    icon: FileText,
    title: 'Invoices are scheduled',
    description:
      'When tuition is due, the system creates scheduled invoices for each due date.',
  },
  {
    icon: Zap,
    title: 'Daily automatic processing',
    description:
      'A daily job runs and picks up all due invoices. No action is required from you.',
  },
  {
    icon: CheckCircle2,
    title: 'Charges run automatically',
    description:
      "Each due invoice is charged to the family's payment method (or wallet) automatically. You don't need to approve or trigger anything.",
  },
  {
    icon: FileText,
    title: 'Monitor revenue',
    description:
      'Payments are processed without manual review. You can track revenue and history in the Payment Dashboard and Invoices.',
  },
];

const MANUAL_JOURNEY_STEPS = [
  {
    icon: FileText,
    title: 'Invoices are scheduled',
    description:
      'When tuition is due, the system creates invoices for each due date. They will not get charged until you approve.',
  },
  {
    icon: Zap,
    title: 'Daily job does not charge until locked',
    description:
      'For Manual Review studios, the daily job leaves Ready charges unprocessed until you Approve & Lock them on Payment Run Review.',
  },
  {
    icon: CheckCircle2,
    title: 'You review and Approve & Lock',
    description:
      'Go to Payments → Payment Run Review. Select Ready charges, then Approve & Lock. Only locked charges run on the next scheduled processing time.',
  },
  {
    icon: FileText,
    title: 'You control timing',
    description:
      'No charge happens until you explicitly approve & lock. You can postpone, unlock (before the cutoff), or add more charges to a selective lock.',
  },
];

interface PaymentProcessingJourneyModalProps {
  variant: PaymentProcessingJourneyVariant;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  /** When false, the option is already selected – confirm button is disabled and no API call should be made. */
  isSelectionChange: boolean;
  /** When provided and variant is auto, shows a link to preview the invoices page (upcoming & scheduled) without opting in. */
  onPreviewInvoices?: () => void;
}

export function PaymentProcessingJourneyModal({
  variant,
  open,
  onOpenChange,
  onConfirm,
  isSelectionChange,
  onPreviewInvoices,
}: PaymentProcessingJourneyModalProps) {
  const isAuto = variant === 'auto';
  const steps = isAuto ? AUTO_JOURNEY_STEPS : MANUAL_JOURNEY_STEPS;
  const title = isAuto
    ? 'How Auto Process Payments works'
    : 'How Manual Review works';
  const confirmLabel = isAuto ? 'Use Auto Process' : 'Use Manual Review';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {isAuto
              ? 'When enabled, scheduled payments are processed automatically without any action from you.'
              : 'When enabled, payments stay pending until you review and approve them in Pending Review.'}
          </DialogDescription>
        </DialogHeader>
        <ol className='space-y-4 py-2'>
          {steps.map((step) => {
            const Icon = step.icon;
            return (
              <li key={step.title} className='flex gap-3'>
                <div className='flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10'>
                  <Icon className='size-4 text-primary' />
                </div>
                <div className='space-y-0.5'>
                  <p className='font-medium leading-tight'>{step.title}</p>
                  <p className='text-sm text-muted-foreground'>
                    {step.description}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
        {variant === 'manual' && (
          <p className='text-sm text-muted-foreground'>
            Review and approve charges in{' '}
            <Link
              to='/payment/payment-run-review'
              className='font-medium text-primary underline underline-offset-2 hover:no-underline'
              onClick={() => onOpenChange(false)}
            >
              Payments → Payment Run Review
            </Link>
            .
          </p>
        )}
        {variant === 'auto' && onPreviewInvoices && (
          <p className='text-sm text-muted-foreground'>
            Want to see what you&apos;d get?{' '}
            <button
              type='button'
              onClick={() => {
                onPreviewInvoices();
                onOpenChange(false);
              }}
              className='font-medium text-primary underline underline-offset-2 hover:no-underline'
            >
              Preview upcoming & scheduled invoices
            </button>
          </p>
        )}
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!isSelectionChange}
            title={
              !isSelectionChange
                ? 'This option is already selected'
                : undefined
            }
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
